import { NotificationWorkflow, notificationWorkflows } from '../workflows';
import {
  NotificationPreferencesValues,
  WorkflowChannelPrefs,
} from './notification-preferences-types';

/**
 * Default toggles for channels a workflow supports (unsupported channels omitted).
 */
export function createDefaultChannelPrefs(workflow: NotificationWorkflow): WorkflowChannelPrefs {
  const prefs: WorkflowChannelPrefs = {};
  if (workflow.channels.inApp) {
    prefs.inAppFeed = true;
  }
  if (workflow.channels.email) {
    prefs.email = true;
  }
  return prefs;
}

/**
 * Default preference matrix for all registered workflows (supported channels only).
 */
export function createDefaultNotificationPreferences(): NotificationPreferencesValues {
  return notificationWorkflows.reduce((acc, workflow) => {
    acc[workflow.key] = createDefaultChannelPrefs(workflow);
    return acc;
  }, {} as NotificationPreferencesValues);
}

/**
 * Reads channel toggles for one workflow from a Knock preference set entry.
 * Missing `channel_types` keys fall back; unsupported channels stay omitted.
 */
export function readWorkflowChannelPrefs(
  setting: unknown,
  workflow: NotificationWorkflow,
  fallback: WorkflowChannelPrefs = createDefaultChannelPrefs(workflow),
): WorkflowChannelPrefs {
  const prefs: WorkflowChannelPrefs = {};

  if (setting == null) {
    return { ...fallback };
  }

  if (typeof setting === 'boolean') {
    if (workflow.channels.inApp) {
      prefs.inAppFeed = setting;
    }
    if (workflow.channels.email) {
      prefs.email = setting;
    }
    return prefs;
  }

  if (typeof setting === 'object' && setting !== null && 'channel_types' in setting) {
    const channelTypes = (setting as { channel_types?: Record<string, unknown> }).channel_types;
    if (workflow.channels.inApp) {
      prefs.inAppFeed = resolveChannelFlag(channelTypes?.in_app_feed, fallback.inAppFeed ?? true);
    }
    if (workflow.channels.email) {
      prefs.email = resolveChannelFlag(channelTypes?.email, fallback.email ?? true);
    }
    return prefs;
  }

  return { ...fallback };
}

function resolveChannelFlag(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** Knock `channel_types` map with only keys the workflow supports. */
export type KnockWorkflowChannelTypes = {
  in_app_feed?: boolean;
  email?: boolean;
};

/**
 * Builds Knock setPreferences payload from the form values.
 * Only includes `channel_types` keys supported by each workflow.
 */
export function toKnockWorkflowPreferences(
  values: NotificationPreferencesValues,
): Record<string, { channel_types: KnockWorkflowChannelTypes }> {
  return notificationWorkflows.reduce(
    (acc, workflow) => {
      const prefs = values[workflow.key] ?? createDefaultChannelPrefs(workflow);
      const channelTypes: KnockWorkflowChannelTypes = {};

      if (workflow.channels.inApp) {
        channelTypes.in_app_feed = prefs.inAppFeed ?? true;
      }
      if (workflow.channels.email) {
        channelTypes.email = prefs.email ?? true;
      }

      acc[workflow.key] = { channel_types: channelTypes };
      return acc;
    },
    {} as Record<string, { channel_types: KnockWorkflowChannelTypes }>,
  );
}
